<template>
  <div class="fill-height d-flex align-center justify-center" style="flex-direction: column;">
    <AppLoader v-if="isFilePending" />
    <template v-else-if="fileErrorState !== null">
      <div class="d-flex flex-column align-center justify-center ga-8">
        <div class="rounded-circle bg-grey-100 d-flex align-center justify-center" style="width: 80px; height: 80px;">
          <app-material-icon name="close" color="grey-600" :size="38" />
        </div>

        <div class="d-flex flex-column align-center justify-center ga-3">
          <h2 class="text-center text-grey-900 font-weight-bold">{{ $t(`login.views.DownloadFromToken.errors.${fileErrorState}.title`) }}</h2>
          <p v-html="$t(`login.views.DownloadFromToken.errors.${fileErrorState}.message`)" class="text-center"></p>
        </div>

        <app-button
          v-if="fileErrorState !== FILE_ERROR_STATES.NOT_WATERMARKABLE_FILE"
          data-locator="documents-page-button"
          type="primary"
          @click="onDocumentsButtonClick"
        >
          {{ $t(`login.views.DownloadFromToken.errors.${fileErrorState}.buttonText`)}}
        </app-button>
      </div>
    </template>
    <template v-else>
      <img
        src="@/assets/img/closd_logo.svg"
        width="150"
        alt="Closd Logo"
      />
      <form
        v-if="isUsingFormSubmit"
        ref="fileDownloadForm"
        :action="link"
        method="post"
      >
      </form>
      <span class="clickable mt-4" @click="downloadFile">
        {{ $t('login.views.DownloadFromToken.clickForDl') }}
      </span>
    </template>
  </div>
</template>

<script>
  import AppLoader from '@/common/AppLoader.vue'
  import { ACCEPTED_DOWNLOAD_TYPES } from '@/common/constants'
  import { extractFileNameFromContentDisposition, fetchAndDownloadFile } from '@/common/utils/download'
  import { toast } from '@/common/utils/toast'
  import { EXPIRED_LINK_ROUTE_NAME, ROOM_DOCUMENTS_ROUTE_NAME } from '@/router/routerData'
  import filesService from '@/services/files.service'

  const FILE_ERROR_STATES = {
    NOT_FOUND: 'NOT_FOUND',
    NO_DOWNLOAD_RIGHT: 'NO_DOWNLOAD_RIGHT',
    NOT_WATERMARKABLE_FILE: 'NOT_WATERMARKABLE_FILE',
    GENERIC: 'GENERIC',
  }

  export default {
    name: 'DownloadFromToken',
    components: { AppLoader },
    props: {
      mnemo: String,
      token: {
        type: String,
      },
      fileId: {
        type: String,
      },
    },
    data() {
      return {
        isFilePending: false,
        fileErrorState: null,
        FILE_ERROR_STATES,
      }
    },
    computed: {
      link () {
        return `${import.meta.env.VUE_APP_API_URL}${this.$route.path}`
      },
      isUsingFormSubmit () {
        return !this.token
      },
    },
    mounted() {
      this.downloadFile()
    },
    methods: {
      async downloadFile () {
        if (this.isUsingFormSubmit) {
          try {
            const fileResponse = await filesService.getFile(this.mnemo, this.fileId, { watermarkInfos: true })
            const file = fileResponse.data

            if (file.isWatermarkEnabledForUser && !file.shouldApplyWatermark) {
              this.fileErrorState = FILE_ERROR_STATES.NOT_WATERMARKABLE_FILE
              return
            }

            if (!file.canDownload) {
              this.fileErrorState = FILE_ERROR_STATES.NO_DOWNLOAD_RIGHT
              return
            }
          } catch (error) {
            if (error.response?.status === 404) {
              this.fileErrorState = FILE_ERROR_STATES.NOT_FOUND
            } else {
              this.fileErrorState = FILE_ERROR_STATES.GENERIC
            }
            return
          }

          await this.submitFormWithAuth()
        } else {
          this.getFileOrRedirect()
        }
      },
      async submitFormWithAuth () {
        this.isFilePending = true
        try {
          await fetchAndDownloadFile(this.link)
        } catch (error) {
          console.error('Download error:', error)
          toast.error(this.$t('login.views.DownloadFromToken.genericError'))
        } finally {
          this.isFilePending = false
        }
      },
      async getFileOrRedirect () {
        this.isFilePending = true
        try {
          const response = await fetch(this.link, {
            method: 'POST',
            credentials: 'include',
          })

          if (!response.ok) {
            await this.handleDownloadErrorResponse(response)
            return
          }

          if (
            response.body
            && ACCEPTED_DOWNLOAD_TYPES.includes(response.headers.get('content-type'))
          ) {
            const contentType = response.headers.get('content-type')
            const blob = new Blob([await response.blob()], { type: contentType })
            const url = globalThis.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url

            const fileName = extractFileNameFromContentDisposition(response.headers.get('content-disposition'), 'document.zip')
            link.setAttribute('download', fileName)
            document.body.appendChild(link)
            link.click()
            link.remove()
            setTimeout(() => globalThis.URL.revokeObjectURL(url), 100)
          } else {
            toast.error(this.$t('login.views.DownloadFromToken.invalidFile'))
          }
        } catch (error) {
          console.error('error:', error)

          toast.error(this.$t('login.views.DownloadFromToken.genericError'))
        } finally {
          this.isFilePending = false
        }
      },
      async handleDownloadErrorResponse (response) {
        if (response.status === 401) {
          try {
            const errorData = await response.json()
            if (errorData?.errorCode === 'ERR_TOKEN_EXPIRED') {
              this.$router.replace({
                name: EXPIRED_LINK_ROUTE_NAME,
                query: {
                  expirationDate: errorData.expirationDate,
                  expirationHour: errorData.expirationHour,
                },
              })
              return
            }
          } catch (error) {
            console.error('Failed to parse download error response:', error)
          }
        }

        if (response.status === 404) {
          toast.error(this.$t('login.views.DownloadFromToken.notFound'))
          return
        }

        toast.error(this.$t('login.views.DownloadFromToken.genericError'))
      },
      onDocumentsButtonClick () {
        this.$router.push({ name: ROOM_DOCUMENTS_ROUTE_NAME, params: { mnemo: this.mnemo, roomType: '_' } })
      },
    },
  }
</script>
